import React, { useEffect, useState } from 'react';
import { getSharedExpenditure, getSharedBatchExpenditure } from '../services/api';
import ScoreLegend from './ScoreLegend';

export default function SharedExpenditure({ requestId, batchId, expId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedAward, setExpandedAward] = useState(null);
  const [showUnmatched, setShowUnmatched] = useState(false);
  const [expandedCandidate, setExpandedCandidate] = useState(null);
  const [copied, setCopied] = useState(false);

  const isBatch = !!batchId;

  useEffect(() => {
    const fetch = isBatch
      ? getSharedBatchExpenditure(batchId, expId)
      : getSharedExpenditure(requestId, expId);
    fetch
      .then(setData)
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [requestId, batchId, expId, isBatch]);

  function handleCopyLink() {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  if (loading) {
    return (
      <div className="processing-indicator" style={{ marginTop: 80 }}>
        <div className="spinner" />
        <span>Loading result…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div className="error-box" style={{ display: 'inline-block', padding: '16px 24px', fontSize: 14 }}>
          {error === 'Authentication required'
            ? 'This link requires you to be logged in. Please sign in to view.'
            : `Could not load result: ${error}`}
        </div>
      </div>
    );
  }

  const {
    expenditure: exp,
    matched_awards: matched = [],
    unmatched_awards: unmatched = [],
    unrestricted_awards: unrestricted = [],
    candidate_count,
    matching_mode: mode,
    processing_time_ms,
    expenditure_matched,
    unmatched_expenditure_reason,
    // batch-specific
    batch_id,
    batch_name,
    completed_at,
  } = data;

  const topMatch = matched.find(a => a.rank === 1);

  return (
    <div className="app-shell">
      {/* Header */}
      <header className="app-header">
        <div className="app-logo">
          <div className="app-logo-icon">S</div>
          <div>
            <h1>Gift Award Matcher</h1>
            <span>Stanford University &mdash; {isBatch ? 'Shared Batch Expenditure' : 'Shared Expenditure Result'}</span>
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <ScoreLegend />
          <button
            className="detail-toggle-btn"
            onClick={handleCopyLink}
            style={{ display: 'flex', alignItems: 'center', gap: 5 }}
          >
            {copied ? '✓ Copied!' : '🔗 Copy Link'}
          </button>
        </div>
      </header>

      <div className="app-content">
        <div className="detail-page">
          {/* Result header bar */}
          <div className="detail-bar">
            <span className="mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {isBatch
                ? (batch_name || `Batch ${batch_id}`)
                : requestId?.substring(0, 20)}
            </span>
            <span className={`badge completed`}>{isBatch ? 'COMPLETE' : 'COMPLETED'}</span>
            <span className={`badge ${mode === 'vector' ? 'vector-mode' : mode === 'hybrid' ? 'hybrid-mode' : mode === 'vector-llm' ? 'vector-llm-mode' : 'llm-mode'}`}>
              {(mode || 'llm').toUpperCase()}
            </span>
            {processing_time_ms != null && (
              <span className="processing-time">{processing_time_ms}ms</span>
            )}
            {expenditure_matched ? (
              <span style={{ color: 'var(--green)', fontWeight: 600, fontSize: 12 }}>
                {matched.length} match{matched.length !== 1 ? 'es' : ''}
              </span>
            ) : (
              <span style={{ color: 'var(--red)', fontWeight: 600, fontSize: 12 }}>No match</span>
            )}
          </div>

          {/* Expenditure summary card */}
          <div className="card" style={{ padding: 14, marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'baseline', marginBottom: 10 }}>
              <span className="mono" style={{ fontWeight: 700, fontSize: 14 }}>{exp.exp_id}</span>
              {exp.amt != null && (
                <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--accent-hover)' }}>
                  ${Number(exp.amt).toLocaleString()}
                </span>
              )}
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{exp.exp_cat || ''}</span>
              {exp.awd_num && (
                <span className="mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  Award hint: {exp.awd_num}
                </span>
              )}
            </div>
            <div className="kv-grid" style={{ fontSize: 12 }}>
              <KV label="Type" value={exp.exp_type} />
              <KV label="Organization" value={exp.task_org_name} />
              <KV label="Project" value={exp.proj_name} />
              <KV label="Task" value={exp.task_name} />
              {exp.exp_txn_long_desc && (
                <div className="kv-row" style={{ gridColumn: '1 / -1' }}>
                  <span className="kv-label">Description</span>
                  <span className="kv-value" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {exp.exp_txn_long_desc}
                  </span>
                </div>
              )}
            </div>
            {topMatch && (
              <div style={{ marginTop: 10, padding: '6px 10px', background: 'rgba(16,185,129,0.08)', borderRadius: 6, fontSize: 12 }}>
                <strong style={{ color: 'var(--green)' }}>Top Match:</strong>{' '}
                <span className="mono" style={{ fontWeight: 700 }}>{topMatch.awd_num}</span>
                {topMatch.awd_name && <span style={{ marginLeft: 6, color: 'var(--text-muted)' }}>{topMatch.awd_name}</span>}
                <span style={{ marginLeft: 8, color: 'var(--accent-hover)', fontWeight: 700 }}>
                  Score: {topMatch.total_score?.toFixed(2)}
                </span>
              </div>
            )}
          </div>

          {/* Matched Awards */}
          {matched.length > 0 && (
            <div className="card" style={{ padding: 14, marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Matched Awards ({matched.length})
              </div>
              <div className="data-table-wrap">
                <table className="data-table compact">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Award</th>
                      <th>Name</th>
                      <th>Nm</th>
                      <th>Pur</th>
                      <th>Prg</th>
                      <th>Rst</th>
                      <th>Age</th>
                      <th>{mode === 'vector' ? 'Vec' : mode === 'hybrid' ? 'Hyb' : mode === 'vector-llm' ? 'V+L' : 'Sem'}</th>
                      <th>Total</th>
                      <th>Rule</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matched.map(a => (
                      <React.Fragment key={a.awd_num}>
                        <tr
                          className={expandedAward === a.awd_num ? 'row-expanded' : ''}
                          onClick={() => setExpandedAward(expandedAward === a.awd_num ? null : a.awd_num)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>
                            <span className={`rank-dot ${a.rank === 1 ? 'gold' : a.rank === 2 ? 'silver' : a.rank === 3 ? 'bronze' : 'default'}`}>
                              {a.rank}
                            </span>
                          </td>
                          <td className="mono" style={{ fontWeight: 600 }}>{a.awd_num}</td>
                          <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.awd_name}</td>
                          <td>{fmtS(a.name_match_score)}</td>
                          <td>{fmtS(a.purpose_match_score)}</td>
                          <td>{fmtS(a.program_match_score)}</td>
                          <td>{fmtS(a.restriction_score)}</td>
                          <td>{fmtS(a.age_score)}</td>
                          <td>{fmtS(a.semantic_score)}</td>
                          <td style={{ fontWeight: 800, color: 'var(--accent-hover)', whiteSpace: 'nowrap' }}>
                            {a.total_score?.toFixed(2)}
                            {a.confidence && (
                              <span className={`confidence-badge ${a.confidence}`} style={{ marginLeft: 4 }}>
                                {a.confidence}
                              </span>
                            )}
                          </td>
                          <td style={{ fontSize: 10, color: 'var(--text-muted)' }}>{getRuleLabel(a)}</td>
                        </tr>

                        {expandedAward === a.awd_num && (
                          <tr className="expanded-row">
                            <td colSpan={11}>
                              <div className="expanded-detail">
                                <div className="kv-grid" style={{ fontSize: 12 }}>
                                  <KV label="Organization" value={a.awd_org} />
                                  <KV label="Start Date" value={a.awd_start} />
                                  <KV label="Purpose" value={a.awd_purpose} />
                                  <KV label="Restriction" value={a.awd_restrict} />
                                  <KV label="Control" value={a.awd_ctrl} />
                                  <KV label="Employee" value={a.awd_role_emp} />
                                </div>
                                {a.awd_fund_auth && (
                                  <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
                                    <strong>Fund Auth:</strong> {a.awd_fund_auth}
                                  </div>
                                )}
                                {a.match_reasons?.length > 0 && (
                                  <div style={{ marginTop: 8 }}>
                                    <div className="match-evidence-header">
                                      Match Evidence
                                      {a.confidence && (
                                        <span className={`confidence-badge ${a.confidence}`} style={{ marginLeft: 6 }}>
                                          {a.confidence} confidence
                                        </span>
                                      )}
                                    </div>
                                    <ol className="match-reasons-list">
                                      {a.match_reasons.map((reason, i) => (
                                        <li key={i} className="match-reason-item">{reason}</li>
                                      ))}
                                    </ol>
                                  </div>
                                )}
                                {a.matched_fields?.length > 0 && (
                                  <div className="matched-fields-list" style={{ marginTop: 6 }}>
                                    {a.matched_fields.map((f, i) => <span key={i} className="field-tag">{f}</span>)}
                                  </div>
                                )}
                                {a.explanation && (
                                  <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                    <strong style={{ fontStyle: 'normal', color: 'var(--accent-hover)' }}>AI:</strong> {a.explanation}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* No match */}
          {!expenditure_matched && (
            <div className="warning-box" style={{ padding: '10px 14px', fontSize: 13 }}>
              No matching awards found for this expenditure.
              {unmatched_expenditure_reason && <span> {unmatched_expenditure_reason}</span>}
            </div>
          )}

          {/* All candidates evaluated */}
          {(unmatched.length > 0 || unrestricted.length > 0) && (
            <div className="card" style={{ padding: 14, marginBottom: 12 }}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setShowUnmatched(v => !v)}
              >
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {showUnmatched ? '▼' : '▶'} All Candidates Evaluated ({matched.length + unmatched.length + unrestricted.length})
                </span>
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                  — {matched.length} matched · {unmatched.length} not matched{unrestricted.length > 0 ? ` · ${unrestricted.length} unrestricted (excluded)` : ''}
                </span>
              </div>
              {showUnmatched && (
                <div className="data-table-wrap" style={{ marginTop: 8 }}>
                  <table className="data-table compact">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Award</th>
                        <th>Name</th>
                        <th>Org</th>
                        <th>Purpose</th>
                        <th>Restriction</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matched.map(a => (
                        <React.Fragment key={`m-${a.awd_num}`}>
                          <tr
                            style={{ background: 'rgba(16,185,129,0.05)', cursor: 'pointer' }}
                            onClick={() => setExpandedCandidate(expandedCandidate === a.awd_num ? null : a.awd_num)}
                          >
                            <td><span style={{ fontSize: 10, color: 'var(--green)', fontWeight: 700 }}>#{a.rank} matched {expandedCandidate === a.awd_num ? '▼' : '▶'}</span></td>
                            <td className="mono" style={{ fontWeight: 600 }}>{a.awd_num}</td>
                            <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.awd_name}</td>
                            <td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10 }}>{a.awd_org}</td>
                            <td style={{ fontSize: 10, color: 'var(--text-muted)' }}>{a.awd_purpose}</td>
                            <td style={{ fontSize: 10, color: 'var(--text-muted)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.awd_restrict}</td>
                          </tr>
                          {expandedCandidate === a.awd_num && (
                            <tr className="expanded-row">
                              <td colSpan={6}>
                                <div className="expanded-detail">
                                  <div className="kv-grid" style={{ fontSize: 12 }}>
                                    <KV label="Organization" value={a.awd_org} />
                                    <KV label="Start Date" value={a.awd_start} />
                                    <KV label="Purpose" value={a.awd_purpose} />
                                    <KV label="Restriction" value={a.awd_restrict} />
                                    <KV label="Control" value={a.awd_ctrl} />
                                    <KV label="Employee" value={a.awd_role_emp} />
                                    <KV label="Score" value={a.total_score?.toFixed(2)} />
                                  </div>
                                  {a.awd_fund_auth && <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}><strong>Fund Auth:</strong> {a.awd_fund_auth}</div>}
                                  {a.match_reasons?.length > 0 && (
                                    <div style={{ marginTop: 8 }}>
                                      <div className="match-evidence-header">Match Evidence</div>
                                      <ol className="match-reasons-list">
                                        {a.match_reasons.map((r, i) => <li key={i} className="match-reason-item">{r}</li>)}
                                      </ol>
                                    </div>
                                  )}
                                  {a.explanation && <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}><strong style={{ fontStyle: 'normal', color: 'var(--accent-hover)' }}>AI:</strong> {a.explanation}</div>}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                      {unmatched.map((a, i) => (
                        <React.Fragment key={`u-${a.awd_num || i}`}>
                          <tr
                            style={{ opacity: 0.75, cursor: 'pointer' }}
                            onClick={() => setExpandedCandidate(expandedCandidate === `u-${a.awd_num || i}` ? null : `u-${a.awd_num || i}`)}
                          >
                            <td><span style={{ fontSize: 10, color: 'var(--text-muted)' }}>not matched {expandedCandidate === `u-${a.awd_num || i}` ? '▼' : '▶'}</span></td>
                            <td className="mono">{a.awd_num}</td>
                            <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.awd_name}</td>
                            <td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10 }}>{a.awd_org}</td>
                            <td style={{ fontSize: 10, color: 'var(--text-muted)' }}>{a.awd_purpose}</td>
                            <td style={{ fontSize: 10, color: 'var(--text-muted)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.awd_restrict}</td>
                          </tr>
                          {expandedCandidate === `u-${a.awd_num || i}` && (
                            <tr className="expanded-row">
                              <td colSpan={6}>
                                <div className="expanded-detail">
                                  <div className="kv-grid" style={{ fontSize: 12 }}>
                                    <KV label="Organization" value={a.awd_org} />
                                    <KV label="Start Date" value={a.awd_start} />
                                    <KV label="Purpose" value={a.awd_purpose} />
                                    <KV label="Restriction" value={a.awd_restrict} />
                                    <KV label="Control" value={a.awd_ctrl} />
                                    <KV label="Employee" value={a.awd_role_emp} />
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                      {unrestricted.map((a, i) => (
                        <tr key={`r-${a.awd_num || i}`} style={{ opacity: 0.5 }}>
                          <td><span style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>unrestricted</span></td>
                          <td className="mono">{a.awd_num}</td>
                          <td colSpan={4} style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>excluded from scoring (unrestricted award)</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div style={{ marginTop: 24, paddingTop: 12, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              Stanford University · Gift Award Matcher · {mode?.toUpperCase()} mode
              {completed_at && ` · Matched ${new Date(completed_at).toLocaleString()}`}
            </span>
            <button
              className="detail-toggle-btn"
              onClick={handleCopyLink}
              style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}
            >
              {copied ? '✓ Link Copied!' : '🔗 Share this result'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function KV({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <div className="kv-row">
      <span className="kv-label">{label}</span>
      <span className="kv-value">{value}</span>
    </div>
  );
}

function fmtS(val) {
  if (val == null) return <span style={{ color: 'var(--text-muted)' }}>-</span>;
  const n = typeof val === 'number' ? val : parseFloat(val);
  if (isNaN(n)) return <span style={{ color: 'var(--text-muted)' }}>-</span>;
  const display = Number.isInteger(n) ? n.toString() : n.toFixed(2);
  return <span style={{ fontWeight: n > 0 ? 700 : 400, color: n > 0 ? 'var(--green)' : 'var(--text-muted)' }}>{display}</span>;
}

function getRuleLabel(award) {
  const hasName = (award.name_match_score || 0) > 0;
  const hasPurpose = (award.purpose_match_score || 0) > 0;
  const hasProgram = (award.program_match_score || 0) > 0;
  if (hasName && hasPurpose && hasProgram) return 'N+P+Prg';
  if (hasName && (hasPurpose || hasProgram)) return `N+${hasPurpose ? 'P' : 'Prg'}`;
  if (hasName) return 'Name';
  if (hasPurpose && hasProgram) return 'P+Prg';
  if (hasPurpose) return 'Purpose';
  if (hasProgram) return 'Program';
  return 'Semantic';
}
